import React, { Component } from 'react';
import { connect } from 'react-redux';

import { GeckoToastService, GeckoModalService, GeckoModal } from 'ui';

import GeckoRequest from 'services/gecko-request';
import GeckoLocation from 'services/gecko-location';
import { renderError } from 'services/gecko-error';

import StepContainer from './components/StepContainer';
import NameWorkflow from './components/steps/NameWorkflow';
import TriggerWorkflow from './components/steps/TriggerWorkflow';
import AddActions from './components/steps/AddActions';
import AddConditions from './components/steps/AddConditions';
import ConfirmSetLive from '../../components/ConfirmSetLive';
import UnsavedChanges from 'components/UnsavedChanges';
import { GeckoLoadingService, GeckoAlert } from 'ui';

class Workflow extends Component {
  state = {
    step1value: undefined,
    _step1value: undefined,
    step2value: undefined,
    _step2value: undefined,
    step3value: undefined,
    _step3value: undefined,
    step4value: undefined,
    _step4value: undefined,
    dirty: false,
    currentStep: 1,
  };

  componentDidMount() {
    if (!this.props.workflow) {
      return;
    }
    if (this.props.workflow.draft) {
      return this.setState(
        Object.assign({}, this.props.workflow.draft, { dirty: false })
      );
    }
    const { name, conditions, actions, trigger } = this.props.workflow;
    this.setState({
      step1value: name,
      _step1value: name,
      step2value: trigger,
      _step2value: trigger,
      step3value: conditions, // conditions[1] is legacy from when we were checking account_id as a condition
      _step3value: conditions,
      step4value: actions,
      _step4value: actions,
      currentStep: 1,
    });
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    if (newProps.workflow) {
      if (newProps.workflow.draft) {
        return this.setState(
          Object.assign({}, newProps.workflow.draft, { dirty: false })
        );
      }
      const { name, conditions, actions, trigger } = newProps.workflow;
      this.setState({
        step1value: name,
        _step1value: name,
        step2value: trigger,
        _step2value: trigger,
        step3value: conditions, // conditions[1] is legacy from when we were checking account_id as a condition
        _step3value: conditions,
        step4value: actions,
        _step4value: actions,
      });
    } else {
      this.setState({
        step1value: undefined,
        _step1value: undefined,
        step2value: undefined,
        _step2value: undefined,
        step3value: undefined,
        _step3value: undefined,
        step4value: undefined,
        _step4value: undefined,
        currentStep: 1,
      });
    }
  }

  handleNameChange(event) {
    this.setState({ _step1value: event.target.value, dirty: true });
  }
  applyNameChange() {
    this.setState({ step1value: this.state._step1value, dirty: true });
    this.nextStep();
  }

  handleTriggerChange(newTrigger) {
    this.setState({
      _step2value: newTrigger,
      _step3value: undefined,
      step3value: undefined,
      _step4value: undefined,
      step4value: undefined,
      dirty: true,
    });
  }
  applyTriggerChange() {
    this.setState({ step2value: this.state._step2value, dirty: true });
    this.nextStep();
  }

  handleConditionsChange(newConditions) {
    this.setState({ _step3value: newConditions, dirty: true });
  }
  applyConditionsChange() {
    this.setState({ step3value: this.state._step3value, dirty: true });
    this.nextStep();
  }

  stripStyleFromBlockWithTags = (blocks) => {
    const getTagLocations = (text) => {
      const formatTagRegex = `{{#[^}}]*}}.*{{\/[^}}]*}}`;
      const regularTagRegex = `{{[^}}]*}}`;
      const regex = new RegExp(`${formatTagRegex}|${regularTagRegex}`, 'g');
      let matchArr, start;
      let tagLocations = [];
      while ((matchArr = regex.exec(text)) !== null) {
        start = matchArr.index;
        tagLocations.push([start, start + matchArr[0].length]);
      }
      return tagLocations;
    };

    return blocks.map((block) => {
      const containsTags = getTagLocations(block.text).length;
      return {
        ...block,
        inlineStyleRanges: containsTags ? [] : block.inlineStyleRanges,
      };
    });
  };

  handleActionsChange(newActions) {
    const processedActions = newActions.map((action) => {
      if (action.type === 'prechat_message') {
        if (action.value?.message?.blocks) {
          return {
            ...action,
            value: {
              ...action.value,
              message: {
                ...action.value.message,
                blocks: this.stripStyleFromBlockWithTags(
                  action.value.message.blocks
                ),
              },
            },
          };
        }
        return action;
      }
      if (action.type === 'prechat_prompt' || action.type === 'send_message') {
        if (action.value?.blocks) {
          return {
            ...action,
            value: {
              ...action.value,
              blocks: this.stripStyleFromBlockWithTags(action.value.blocks),
            },
          };
        }
        return action;
      }
      return action;
    });

    this.setState({ _step4value: processedActions, dirty: true });
  }
  applyActionsChange() {
    this.setState({ step4value: this.state._step4value, dirty: true });
    this.nextStep();
  }

  nextStep() {
    this.setState({
      currentStep: this.state.currentStep + 1,
    });
  }

  isStep(num) {
    return this.state.currentStep === num;
  }

  getProgress(num) {
    const _key = `_step${num}value`;
    const _value = this.state[_key];
    const key = `step${num}value`;
    const value = this.state[key];
    if (this.state.currentStep === num) {
      return 'in-progress';
    }
    if (_value !== undefined && value === undefined) {
      return 'in-progress';
    }
    if (value !== undefined) {
      return 'complete';
    }
    return 'incomplete';
  }

  goToStep(num) {
    return this.setState({ currentStep: num });
  }

  buildWorkflow() {
    const workflow = {
      name: this.state.step1value,
      trigger: this.state.step2value,
      actions: this.state.step4value.filter(Boolean),
      conditions: this.state.step3value,
    };
    const postURL =
      this.props.workflowId !== 'new'
        ? '/workflow/' + this.props.workflowId
        : '/workflow';
    return new GeckoLoadingService()
      .resolve(GeckoRequest.post(postURL, workflow))
      .then((workflow) => {
        this.setState({ dirty: false });
        new GeckoToastService().success('Workflow saved');
      })
      .catch(renderError);
  }

  saveDraft() {
    const workflow = this.state;
    if (!workflow.step1value && !workflow._step1value) {
      return new GeckoToastService().error(
        'A name must be provided to save a workflow draft'
      );
    }
    const postURL =
      this.props.workflowId !== 'new'
        ? '/workflow/draft/' + this.props.workflowId
        : '/workflow/draft';
    new GeckoLoadingService()
      .resolve(GeckoRequest.post(postURL, workflow))
      .then((workflow) => {
        this.setState({ dirty: false });
        new GeckoToastService().success('Workflow draft saved');
        GeckoLocation.goTo('/automation/workflows');
      })
      .catch(renderError);
  }

  renderDialog() {
    let buttons = [];
    let text = '';
    if (
      this.props.workflow &&
      (this.props.workflow.external_id || !this.props.workflow.draft)
    ) {
      // If the workflow is live allow saving/discarding
      buttons = [
        {
          name: 'Discard',
          icon: 'far fa-trash-alt',
          handler: () => new GeckoModalService().close(true),
        },
        {
          name: 'Save',
          type: 'primary',
          icon: 'far fa-save',
          handler: () => {
            return new Promise((resolve) => {
              // Apply all the unsaved changes changes
              this.setState(
                {
                  step1value: this.state._step1value,
                  step2value: this.state._step2value,
                  step3value: this.state._step3value,
                  step4value: this.state._step4value,
                },
                () => {
                  // Save the workflow and close the modal
                  return this.buildWorkflow().finally(() => {
                    resolve();
                    new GeckoModalService().close(true);
                  });
                }
              );
            });
          },
        },
        {
          name: 'Cancel',
          secondary: true,
          icon: 'far fa-times',
          handler: () => new GeckoModalService().close(false),
        },
      ];
      text =
        'You have unsaved changes on your workflow, do you want to discard these changes or save them?';
    } else {
      // If the workflow is not live allow saving draft/discarding
      buttons = [
        {
          name: 'Discard',
          icon: 'far fa-trash-alt',
          type: 'default',
          handler: () => new GeckoModalService().close(true),
        },
        {
          name: 'Save as Draft',
          type: 'primary',
          icon: 'far fa-save',
          handler: () => {
            new GeckoModalService().close(false);
            this.saveDraft();
          },
        },
        {
          name: 'Cancel',
          type: 'default',
          secondary: true,
          icon: 'far fa-times',
          handler: () => new GeckoModalService().close(false),
        },
      ];
      text =
        'You have unsaved changes on your workflow, do you want to discard these changes or save the workflow as a draft?';
    }
    return new GeckoModalService().open(() => (
      <GeckoModal btns={buttons}>{text}</GeckoModal>
    ));
  }

  unfinishedConditions(conditions) {
    if (conditions === undefined) {
      return true;
    }
    if (conditions === null) {
      return false;
    }
    const extractConditions = (group) => {
      return group.conditions.reduce((conditions, condition) => {
        if (condition.is_group) {
          return conditions.concat(extractConditions(condition));
        }
        return conditions.concat(condition);
      }, []);
    };
    const _conditions = extractConditions(conditions);
    if (!_conditions.length) {
      return true;
    } // If they haven't selected any conditions mark it as having unfinished conditions
    return !!_conditions.find((condition) => {
      if (!condition.key || !condition.operator) return true;
      const noValueOperators = [
        'is blank',
        'is not blank',
        'is known',
        'is unknown',
      ];
      if (noValueOperators.includes(condition.operator)) {
        return false;
      }
      return (
        condition.value === undefined ||
        (Array.isArray(condition.value) && !condition.value.length)
      );
    });
  }

  unfinishedActions(actions) {
    if (!actions || !actions.length) {
      return true;
    }
    return !!actions.find((action) => {
      if (!action.type) {
        return false;
      }
      return (
        action.value === undefined ||
        (Array.isArray(action.value) && !action.value.length) ||
        (action.type === 'prechat_message' &&
          (!action.value.message || !action.value.sender)) ||
        (action.type === 'prechat_prompt' && !action.value)
      );
    });
  }

  render() {
    if (!this.props.workflow && this.props.workflowId !== 'new') {
      return (
        <div className="content-padding">
          <GeckoAlert type="warning">
            <h2>Workflow Not Found</h2>
          </GeckoAlert>
        </div>
      );
    }

    return (
      <div className="content-padding WorkflowContainer">
        <UnsavedChanges
          dirty={this.state.dirty}
          customDialog={this.renderDialog.bind(this)}
        >
          <StepContainer
            stepNumber={1}
            name="Name your workflow"
            stepState={this.getProgress(1)}
            expanded={this.isStep(1)}
            onNext={this.applyNameChange.bind(this)}
            onClick={() => this.goToStep(1)}
          >
            <NameWorkflow
              onChange={this.handleNameChange.bind(this)}
              value={this.state._step1value}
            />
          </StepContainer>
          <StepContainer
            stepNumber={2}
            name="When would you like to trigger this workflow?"
            stepState={this.getProgress(2)}
            expanded={this.isStep(2)}
            onNext={this.applyTriggerChange.bind(this)}
            onClick={() => this.goToStep(2)}
          >
            <TriggerWorkflow
              onChange={this.handleTriggerChange.bind(this)}
              value={this.state._step2value}
            />
          </StepContainer>
          <StepContainer
            stepNumber={3}
            name="Add conditions"
            stepState={this.getProgress(3)}
            expanded={this.isStep(3)}
            onNext={this.applyConditionsChange.bind(this)}
            onClick={() => this.goToStep(3)}
          >
            <AddConditions
              onChange={this.handleConditionsChange.bind(this)}
              trigger={this.state._step2value}
              value={this.state._step3value}
            />
          </StepContainer>
          <StepContainer
            stepNumber={4}
            name="Add actions"
            stepState={this.getProgress(4)}
            expanded={this.isStep(4)}
            onNext={this.applyActionsChange.bind(this)}
            onClick={() => this.goToStep(4)}
          >
            <AddActions
              onChange={this.handleActionsChange.bind(this)}
              trigger={this.state._step2value}
              value={this.state._step4value}
            />
          </StepContainer>
          <StepContainer
            stepNumber={5}
            name="Confirm and set live"
            stepState={
              this.props.workflowId === 'new' || this.props.workflow.draft
                ? 'incomplete'
                : 'complete'
            }
            expanded={this.isStep(5)}
            nextDisabled={
              this.state.step1value === undefined ||
              this.state.step2value === undefined ||
              this.unfinishedConditions(this.state.step3value) ||
              this.unfinishedActions(this.state.step4value)
            }
            onSetLive={() =>
              this.buildWorkflow().then(() =>
                GeckoLocation.goTo('/automation/workflows')
              )
            }
            onClick={() => this.goToStep(5)}
          >
            <ConfirmSetLive
              steps={[
                {
                  warning: "You haven't finished naming your workflow.",
                  isValid: this.state.step1value !== undefined,
                },
                {
                  warning:
                    "You haven't finished choosing when to trigger your workflow.",
                  isValid: this.state.step2value !== undefined,
                },
                {
                  warning:
                    "You haven't finished choosing whether your workflow should have any conditions.",
                  isValid: !this.unfinishedConditions(this.state.step3value),
                },
                {
                  warning:
                    "You haven't finished adding actions to your workflow.",
                  isValid: !this.unfinishedActions(this.state.step4value),
                },
              ]}
              readyMessage="It looks like your workflow is ready to go. Good Job!"
              notReadyMessage="It looks like your workflow has some issues"
              changeStep={(num) => this.setState({ currentStep: num })}
            />
          </StepContainer>
        </UnsavedChanges>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    account_id: state.appState.loggedInUser.account_id,
    workflow: state.workflows.find(
      (workflow) => Number(workflow.id) === Number(props.workflowId)
    ),
  };
};

export default connect(mapStateToProps)(Workflow);
